import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Divider,
} from "@mui/material";
import { Save, Cancel } from "@mui/icons-material";
import { useCreateTherapy, useUpdateTherapy } from "../../../stores/MasterTables/therapies.mutations";
import { useTherapyCertifications, useTarifGroups } from "../../../stores/MasterTables/therapies.queries";
import type { TherapyAdmin } from "./types";

interface ModalTherapyProps {
  open: boolean;
  onClose: () => void;
  editItem: TherapyAdmin | null;
}

const emptyForm = {
  id: "",
  abbreviation: "",
  description: "",
  positionNumber: "",
  tarifGroup: "",
  durationMinutes: "",
  certificationId: "",
  inUse: true,
  isAdditional: false,
};

const ModalTherapy: React.FC<ModalTherapyProps> = ({ open, onClose, editItem }) => {
  const createTherapy = useCreateTherapy();
  const updateTherapy = useUpdateTherapy();
  const { data: certifications = [] } = useTherapyCertifications();
  const { data: tarifGroups = [] } = useTarifGroups();

  const [form, setForm] = useState(emptyForm);

  const isEdit = !!editItem;

  useEffect(() => {
    if (open) {
      if (editItem) {
        setForm({
          id: editItem.id || "",
          abbreviation: editItem.abbreviation || "",
          description: editItem.description || "",
          positionNumber: editItem.lineItem?.toString() || "",
          tarifGroup: (typeof editItem.tarifGroup === "object" ? editItem.tarifGroup?.id : editItem.tarifGroup) || "",
          durationMinutes: editItem.duration?.toString() || "",
          certificationId: editItem.therapyCertification?.id || "",
          inUse: editItem.inUse ?? true,
          isAdditional: editItem.isSupplementary ?? false,
        });
      } else {
        setForm(emptyForm);
      }
    }
  }, [open, editItem]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleCheckbox = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.checked }));
  };

  const handleSubmit = async () => {
    const payload: any = {
      id: form.id,
      abbreviation: form.abbreviation,
      description: form.description,
      lineItem: form.positionNumber || undefined,
      tarifGroupId: form.tarifGroup || undefined,
      duration: form.durationMinutes ? parseInt(form.durationMinutes) : undefined,
      therapyCertificationId: form.certificationId || undefined,
      inUse: form.inUse,
      isSupplementary: form.isAdditional,
    };

    try {
      if (isEdit) {
        await updateTherapy.mutateAsync({ id: editItem!.id, data: payload });
      } else {
        await createTherapy.mutateAsync(payload);
      }
      onClose();
    } catch {
      // error handled in mutation
    }
  };

  const isPending = createTherapy.isPending || updateTherapy.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {isEdit ? "Heilmittel bearbeiten" : "Neues Heilmittel anlegen"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isEdit ? `ID: ${editItem?.id}` : "Füllen Sie alle Pflichtfelder aus"}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField
              label="ID *"
              value={form.id}
              onChange={handleChange("id")}
              fullWidth
              size="small"
              disabled={isEdit}
              placeholder="z.B. KG"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Abkürzung *"
              value={form.abbreviation}
              onChange={handleChange("abbreviation")}
              fullWidth
              size="small"
              placeholder="z.B. KG"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Position"
              type="number"
              value={form.positionNumber}
              onChange={handleChange("positionNumber")}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Beschreibung *"
              value={form.description}
              onChange={handleChange("description")}
              fullWidth
              size="small"
              placeholder="z.B. Krankengymnastik"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Tarifgruppe"
              value={form.tarifGroup}
              onChange={handleChange("tarifGroup")}
              fullWidth
              size="small"
              select
            >
              <MenuItem value="">– Keine –</MenuItem>
              {tarifGroups.map((tg: any) => (
                <MenuItem key={tg.id || tg} value={tg.id || tg}>
                  {tg.name || tg.id || tg}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Dauer (Min)"
              type="number"
              value={form.durationMinutes}
              onChange={handleChange("durationMinutes")}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Zertifizierung"
              value={form.certificationId}
              onChange={handleChange("certificationId")}
              fullWidth
              size="small"
              select
            >
              <MenuItem value="">– Keine –</MenuItem>
              {certifications.map((c: any) => (
                <MenuItem key={c.id || c} value={c.id || c}>
                  {c.name || c.id || c}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.inUse}
                  onChange={handleCheckbox("inUse")}
                  color="success"
                />
              }
              label="In Verwendung"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isAdditional}
                  onChange={handleCheckbox("isAdditional")}
                  color="info"
                />
              }
              label="Ist Ergänzung"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} startIcon={<Cancel />} disabled={isPending}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          startIcon={<Save />}
          disabled={isPending || !form.id || !form.abbreviation || !form.description}
        >
          {isPending ? "Speichert…" : isEdit ? "Aktualisieren" : "Anlegen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalTherapy;
